import type { TaskEither } from 'fp-ts/lib/TaskEither';
import type { UpdateDappFormData as UpdateDappDataType } from '../../../../lib/schemas/formSchemas';
import type { TypedError } from '../../../../lib/typedError';
import type { ImageDimensions } from '../../../../utils/imageDimensions';
import * as Form from '../../../Form';
import { DappPreviewPanelWithUrls } from '../../DappPreviewPanelWithUrls';
import { FORM_ID, UpdateDappFormData } from '../constants';
import { AdditionalInfoSection } from '../sections/AdditionalInfoSection';
import { BasicDetailsSection } from '../sections/BasicDetailsSection';
import { EditorsChoiceSection } from '../sections/EditorsChoiceSection';
import { LegalComplianceSection } from '../sections/LegalComplianceSection';
import type {
  DashboardDetailsInputChangeEvent,
  FormState,
  PreviewData,
} from '../types';
import { FormValidityObserver } from './FormValidityObserver';

interface DashboardDetailsFormProps {
  dappId?: string;
  formData: FormState;
  previewPanelData: PreviewData;
  previewDimensions: ImageDimensions | null;
  resolvedDappIconUrl: string | null;
  resolvedBannerUrl: string | null;
  resolvedPreviewUrls: (string | null)[];
  resolvedEditorsChoiceGraphicUrl: string | null;
  existingPreviewUrls: (string | null)[];
  onValidityChange: (isValid: boolean) => void;
  onInputChange: (event: DashboardDetailsInputChangeEvent) => void;
  onPreviewChange: (index: number, file: File | null) => Promise<void>;
  onAddPreviewSlot: () => void;
  onRemovePreviewSlot: (index: number) => void;
  onSingleFileChange: (
    fieldName: 'dappIcon' | 'banner' | 'editorsChoiceGraphic',
    file: File | null,
  ) => Promise<void>;
  onRemoveExistingDappIcon: () => void;
  onRemoveExistingBanner: () => void;
  onRemoveExistingPreview: (index: number) => void;
  onRemoveExistingGraphic: () => void;
  onSubmit: (
    input: TaskEither<TypedError<'InvalidFormData'>, UpdateDappDataType>,
  ) => TaskEither<unknown, unknown>;
}

export function DashboardDetailsForm({
  dappId,
  formData,
  previewPanelData,
  previewDimensions,
  resolvedDappIconUrl,
  resolvedBannerUrl,
  resolvedPreviewUrls,
  resolvedEditorsChoiceGraphicUrl,
  existingPreviewUrls,
  onValidityChange,
  onInputChange,
  onPreviewChange,
  onAddPreviewSlot,
  onRemovePreviewSlot,
  onSingleFileChange,
  onRemoveExistingDappIcon,
  onRemoveExistingBanner,
  onRemoveExistingPreview,
  onRemoveExistingGraphic,
  onSubmit,
}: DashboardDetailsFormProps) {
  return (
    <div className="flex flex-col md:flex-row gap-8 p-8 bg-[#161717] text-white flex-1 overflow-y-auto">
      <div className="flex-1">
        <div className="flex flex-col md:flex-row gap-8">
          <Form.Root
            key={dappId ? `${dappId}-loaded` : 'dashboard-details-form'}
            id={FORM_ID}
            formDataShape={UpdateDappFormData}
            onSubmitData={onSubmit}
            className="flex-1 mb-8"
            validityKey={JSON.stringify(formData)}
          >
            <FormValidityObserver
              onValidityChange={onValidityChange}
              hasDappIcon={!!formData.dappIcon || !!resolvedDappIconUrl}
              hasPreviewImages={
                formData.dappPreviews.some((preview) => preview !== null) ||
                existingPreviewUrls.some((url) => url !== null)
              }
            />
            <div className="w-full max-w-[640px] mx-auto flex flex-col gap-16">
              <div>
                <h1 className="justify-start text-white text-4xl font-medium leading-10">
                  Update your dApp details
                </h1>
                <p className="text-sm text-neutral-400 mt-2">
                  Note: Changes will be reflected in the dapp store after
                  publishing a new release
                </p>
              </div>

              <BasicDetailsSection
                formData={formData}
                previewDimensions={previewDimensions}
                resolvedDappIconUrl={resolvedDappIconUrl}
                resolvedBannerUrl={resolvedBannerUrl}
                resolvedPreviewUrls={resolvedPreviewUrls}
                existingPreviewUrls={existingPreviewUrls}
                onInputChange={onInputChange}
                onSingleFileChange={onSingleFileChange}
                onPreviewChange={onPreviewChange}
                onRemoveExistingDappIcon={onRemoveExistingDappIcon}
                onRemoveExistingBanner={onRemoveExistingBanner}
                onRemoveExistingPreview={onRemoveExistingPreview}
                onAddPreviewSlot={onAddPreviewSlot}
                onRemovePreviewSlot={onRemovePreviewSlot}
              />

              <EditorsChoiceSection
                formData={formData}
                resolvedEditorsChoiceGraphicUrl={
                  resolvedEditorsChoiceGraphicUrl
                }
                onInputChange={onInputChange}
                onSingleFileChange={onSingleFileChange}
                onRemoveExistingGraphic={onRemoveExistingGraphic}
              />

              <AdditionalInfoSection
                formData={formData}
                onInputChange={onInputChange}
              />

              <LegalComplianceSection
                formData={formData}
                onInputChange={onInputChange}
              />

              <Form.Error className="mt-1 text-right" />
            </div>
          </Form.Root>

          <div className="md:w-[300px] flex-shrink-0 xl:mr-48 lg:mr-32">
            <DappPreviewPanelWithUrls
              formData={previewPanelData}
              maxPreviews={formData.dappPreviews.length}
              previewDimensions={previewDimensions}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
